import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarDays,
  Users,
  Megaphone,
  UserPlus,
  Clock,
  ArrowRight,
  Activity,
  CalendarPlus,
  UserRound,
  AlertCircle,
  TrendingUp,
} from 'lucide-react';
import { Button } from '@/components/ui';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAuth } from '@/hooks';
import { appointmentService } from '@/services/appointmentService';
import { patientService } from '@/services/patientService';
import { campaignService } from '@/services/campaignService';
import { ltvService } from '@/services/ltvService';
import type { Appointment } from '@/types/appointment';
import styles from './DashboardPage.module.css';

type BadgeColor = 'green' | 'blue' | 'orange' | 'red';

interface SummaryCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: BadgeColor;
}

const COLOR_MAP: Record<BadgeColor, string> = {
  green: '#00A651',
  blue: '#003B71',
  orange: '#F7941D',
  red: '#DC3545',
};

/**
 * Summary card component for dashboard metrics.
 */
function SummaryCard({ title, value, icon, color }: SummaryCardProps) {
  return (
    <article
      className={styles.summaryCard}
      style={{ borderTopColor: COLOR_MAP[color] }}
    >
      <div className={styles.summaryIcon} style={{ color: COLOR_MAP[color] }} aria-hidden="true">
        {icon}
      </div>
      <div className={styles.summaryInfo}>
        <p className={styles.summaryValue}>{value}</p>
        <p className={styles.summaryTitle}>{title}</p>
      </div>
    </article>
  );
}

/**
 * Formats a numeric value as BRL currency.
 */
function formatCurrencyBRL(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

/**
 * Formats a time string (HH:mm) for display.
 */
function formatTime(time: string): string {
  return time.slice(0, 5);
}

/**
 * Returns today's date as YYYY-MM-DD string.
 */
function getTodayDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Admin dashboard page - landing page after login.
 *
 * Displays:
 * - Welcome message with user name
 * - Summary cards: today's appointments, pending patients, active campaigns, recent leads
 * - Quick action buttons: new appointment, new patient, view schedule
 * - Recent activity feed (latest 10 appointments for today)
 *
 * Route: /admin/dashboard
 */
export function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const todayDate = getTodayDateString();

  const {
    data: dashboard,
    isLoading: isDashboardLoading,
    error: dashboardError,
  } = useQuery({
    queryKey: ['clinic-dashboard', todayDate],
    queryFn: () => appointmentService.getClinicDashboard(todayDate),
  });

  const {
    data: patientsData,
    isLoading: isPatientsLoading,
  } = useQuery({
    queryKey: ['patients-dashboard'],
    queryFn: () => patientService.searchPatients('', 1, 1),
  });

  const {
    data: campaigns = [],
    isLoading: isCampaignsLoading,
  } = useQuery({
    queryKey: ['campaigns-dashboard', { status: 'ACTIVE' }],
    queryFn: () => campaignService.getCampaigns({ status: 'ACTIVE' }),
  });

  const {
    data: ltvDashboard,
    isLoading: isLtvLoading,
  } = useQuery({
    queryKey: ['ltv-dashboard'],
    queryFn: () => ltvService.getDashboard(),
  });

  const isLoading = isDashboardLoading || isPatientsLoading || isCampaignsLoading;

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate],
  );

  const todayAppointmentsCount = dashboard?.totalScheduled ?? 0;
  const confirmedCount = dashboard?.totalConfirmed ?? 0;
  const completedCount = dashboard?.totalCompleted ?? 0;
  const waitingCount = dashboard?.totalWaiting ?? 0;
  const activeCampaignsCount = campaigns.length;
  const totalPatients = patientsData?.totalCount ?? 0;

  // Recent activity: latest 10 appointments from today, sorted by time
  const recentAppointments: Appointment[] = (dashboard?.appointments ?? [])
    .slice()
    .sort((a, b) => (b.startTime ?? '').localeCompare(a.startTime ?? ''))
    .slice(0, 10);

  const getStatusLabel = (status: string): string => {
    const map: Record<string, string> = {
      SCHEDULED: 'Agendado',
      CONFIRMED: 'Confirmado',
      WAITING: 'Em espera',
      IN_PROGRESS: 'Em andamento',
      COMPLETED: 'Concluído',
      CANCELLED: 'Cancelado',
      NO_SHOW: 'Não compareceu',
    };
    return map[status] ?? status;
  };

  const getStatusColor = (status: string): string => {
    const map: Record<string, string> = {
      SCHEDULED: '#17A2B8',
      CONFIRMED: '#28A745',
      WAITING: '#FFC107',
      IN_PROGRESS: '#17A2B8',
      COMPLETED: '#28A745',
      CANCELLED: '#DC3545',
      NO_SHOW: '#DC3545',
    };
    return map[status] ?? '#666666';
  };

  if (dashboardError) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState} role="alert">
          <AlertCircle size={32} aria-hidden="true" />
          <p>Erro ao carregar dados do dashboard. Tente novamente mais tarde.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Welcome header */}
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>
            Ola, {user?.fullName ?? 'Administrador'}!
          </h1>
          <p className={styles.subtitle}>
            Aqui está o resumo do seu dia.
          </p>
        </div>
      </header>

      {/* Summary cards */}
      <section aria-label="Resumo do dia" className={styles.summaryGrid}>
        {isLoading ? (
          <>
            <Skeleton variant="card" height="120px" />
            <Skeleton variant="card" height="120px" />
            <Skeleton variant="card" height="120px" />
            <Skeleton variant="card" height="120px" />
          </>
        ) : (
          <>
            <SummaryCard
              title="Consultas hoje"
              value={todayAppointmentsCount + confirmedCount + waitingCount + completedCount}
              icon={<CalendarDays size={24} />}
              color="blue"
            />
            <SummaryCard
              title="Pacientes cadastrados"
              value={totalPatients}
              icon={<Users size={24} />}
              color="green"
            />
            <SummaryCard
              title="Campanhas ativas"
              value={activeCampaignsCount}
              icon={<Megaphone size={24} />}
              color="orange"
            />
            <SummaryCard
              title="Aguardando atendimento"
              value={waitingCount}
              icon={<Clock size={24} />}
              color="red"
            />
          </>
        )}
      </section>

      {/* Quick actions */}
      <section className={styles.quickActions} aria-label="Ações rápidas">
        <h2 className={styles.sectionTitle}>Ações rápidas</h2>
        <div className={styles.actionsGrid}>
          <Button
            variant="outline"
            leftIcon={<CalendarPlus size={18} />}
            onClick={() => handleNavigate('/admin/schedule')}
          >
            Novo agendamento
          </Button>
          <Button
            variant="outline"
            leftIcon={<UserPlus size={18} />}
            onClick={() => handleNavigate('/admin/patients')}
          >
            Novo paciente
          </Button>
          <Button
            variant="outline"
            leftIcon={<CalendarDays size={18} />}
            onClick={() => handleNavigate('/admin/daily-panel')}
          >
            Painel do dia
          </Button>
        </div>
      </section>

      {/* Recent activity */}
      <section className={styles.activitySection} aria-labelledby="activity-heading">
        <div className={styles.activityHeader}>
          <h2 id="activity-heading" className={styles.sectionTitle}>
            <Activity size={20} aria-hidden="true" />
            Atividade recente
          </h2>
          <Button
            variant="ghost"
            size="sm"
            rightIcon={<ArrowRight size={16} />}
            onClick={() => handleNavigate('/admin/daily-panel')}
          >
            Ver tudo
          </Button>
        </div>

        {isLoading ? (
          <div className={styles.activityList}>
            <Skeleton variant="rectangle" height="60px" />
            <Skeleton variant="rectangle" height="60px" />
            <Skeleton variant="rectangle" height="60px" />
          </div>
        ) : recentAppointments.length === 0 ? (
          <EmptyState
            icon={<CalendarDays size={48} />}
            title="Nenhuma atividade hoje"
            description="Os agendamentos do dia aparecerão aqui."
          />
        ) : (
          <ul className={styles.activityList} role="list">
            {recentAppointments.map((appointment) => (
              <li key={appointment.id} className={styles.activityItem}>
                <div className={styles.activityIcon}>
                  <UserRound size={16} aria-hidden="true" />
                </div>
                <div className={styles.activityContent}>
                  <div className={styles.activityMain}>
                    <span className={styles.activityPatient}>
                      {appointment.patientName}
                    </span>
                    <span className={styles.activityProcedure}>
                      {appointment.procedureTypeName}
                    </span>
                  </div>
                  <div className={styles.activityMeta}>
                    <span className={styles.activityTime}>
                      {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
                    </span>
                    <span
                      className={styles.activityStatus}
                      style={{ color: getStatusColor(appointment.status) }}
                    >
                      {getStatusLabel(appointment.status)}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* LTV: Top Patients */}
      <section className={styles.activitySection} aria-labelledby="ltv-top-heading">
        <h2 id="ltv-top-heading" className={styles.sectionTitle}>
          <TrendingUp size={20} aria-hidden="true" />
          Top Pacientes por LTV
        </h2>
        {isLtvLoading ? (
          <div className={styles.activityList}>
            <Skeleton variant="rectangle" height="60px" />
            <Skeleton variant="rectangle" height="60px" />
            <Skeleton variant="rectangle" height="60px" />
          </div>
        ) : (ltvDashboard?.topPatients ?? []).length === 0 ? (
          <EmptyState
            icon={<UserRound size={48} />}
            title="Sem dados de LTV"
            description="Dados de receita dos pacientes aparecerão aqui."
          />
        ) : (
          <ul className={styles.activityList} role="list">
            {(ltvDashboard?.topPatients ?? []).map((patient) => (
              <li key={patient.patientId} className={styles.activityItem}>
                <div className={styles.activityIcon}>
                  <UserRound size={16} aria-hidden="true" />
                </div>
                <div className={styles.activityContent}>
                  <div className={styles.activityMain}>
                    <span className={styles.activityPatient}>
                      {patient.patientName}
                    </span>
                    <span className={styles.activityProcedure}>
                      {patient.totalAppointments} consultas
                    </span>
                  </div>
                  <div className={styles.activityMeta}>
                    <span className={styles.activityStatus} style={{ color: '#00A651' }}>
                      {formatCurrencyBRL(patient.totalRevenue)}
                    </span>
                    <span className={styles.activityTime}>
                      LTV: {formatCurrencyBRL(patient.estimatedLtv)}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* LTV: At-Risk High-Value Patients */}
      <section className={styles.activitySection} aria-labelledby="ltv-risk-heading">
        <h2 id="ltv-risk-heading" className={styles.sectionTitle}>
          <AlertCircle size={20} aria-hidden="true" />
          Pacientes Alto Valor em Risco
        </h2>
        {isLtvLoading ? (
          <div className={styles.activityList}>
            <Skeleton variant="rectangle" height="60px" />
            <Skeleton variant="rectangle" height="60px" />
          </div>
        ) : (ltvDashboard?.atRiskPatients ?? []).length === 0 ? (
          <EmptyState
            icon={<UserRound size={48} />}
            title="Nenhum paciente em risco"
            description="Pacientes de alto valor sem visitas recentes aparecerão aqui."
          />
        ) : (
          <ul className={styles.activityList} role="list">
            {(ltvDashboard?.atRiskPatients ?? []).map((patient) => (
              <li key={patient.patientId} className={styles.activityItem}>
                <div className={styles.activityIcon} style={{ color: '#DC3545' }}>
                  <AlertCircle size={16} aria-hidden="true" />
                </div>
                <div className={styles.activityContent}>
                  <div className={styles.activityMain}>
                    <span className={styles.activityPatient}>
                      {patient.patientName}
                    </span>
                    <span className={styles.activityProcedure}>
                      LTV: {formatCurrencyBRL(patient.estimatedLtv)}
                    </span>
                  </div>
                  <div className={styles.activityMeta}>
                    <span className={styles.activityTime}>
                      {patient.lastVisitDate
                        ? `Ultima visita: ${new Date(patient.lastVisitDate).toLocaleDateString('pt-BR')}`
                        : 'Sem visitas'}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* LTV: Average by Channel */}
      <section className={styles.activitySection} aria-labelledby="ltv-channel-heading">
        <h2 id="ltv-channel-heading" className={styles.sectionTitle}>
          <Activity size={20} aria-hidden="true" />
          LTV Medio por Canal
        </h2>
        {isLtvLoading ? (
          <div className={styles.activityList}>
            <Skeleton variant="rectangle" height="60px" />
            <Skeleton variant="rectangle" height="60px" />
          </div>
        ) : (ltvDashboard?.channelSummary ?? []).length === 0 ? (
          <EmptyState
            icon={<Activity size={48} />}
            title="Sem dados de canais"
            description="Dados de LTV por canal de aquisicao aparecerão aqui."
          />
        ) : (
          <ul className={styles.activityList} role="list">
            {(ltvDashboard?.channelSummary ?? []).map((ch) => (
              <li key={ch.channel} className={styles.activityItem}>
                <div className={styles.activityIcon}>
                  <Activity size={16} aria-hidden="true" />
                </div>
                <div className={styles.activityContent}>
                  <div className={styles.activityMain}>
                    <span className={styles.activityPatient}>
                      {ch.channel}
                    </span>
                    <span className={styles.activityProcedure}>
                      {ch.totalPatients} pacientes
                    </span>
                  </div>
                  <div className={styles.activityMeta}>
                    <span className={styles.activityStatus} style={{ color: '#003B71' }}>
                      LTV Medio: {formatCurrencyBRL(ch.averageLtv)}
                    </span>
                    <span className={styles.activityTime}>
                      Receita: {formatCurrencyBRL(ch.totalRevenue)}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
